import React, { useState } from "react";
import { Btn, Modal, useToast } from "@app/shared/src/components_v2";
import { emptyOrganizationDraft, organizationDraftError, toOrganization } from "../../helpers/organizationDraft";
import { useOrg } from "../providers/OrgProvider";
import { OrganizationFields } from "./OrganizationFields";

/**
 * Create another organization from wherever the director is — the "+ Add new organization" row
 * at the foot of the topbar picker and the phone's scope sheet both open this.
 *
 * The server makes whoever creates an organization its administrator, so the new one appears in
 * the picker at once and the console switches to it: an empty fleet is the right first view of
 * an organization that has no trucks yet.
 */
export const AddOrganizationDialog: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
    const { createOrg } = useOrg();
    const toast = useToast();
    const [draft, setDraft] = useState(emptyOrganizationDraft);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string>();

    const close = () => {
        if (busy) return;
        setDraft(emptyOrganizationDraft());
        setError(undefined);
        onClose();
    };

    const create = async () => {
        if (busy) return;
        const problem = organizationDraftError(draft);
        if (problem) {
            setError(problem);
            return;
        }
        setBusy(true);
        setError(undefined);
        try {
            await createOrg(toOrganization(draft));
            toast(`${draft.name.trim()} created — you're now looking at it`);
            setDraft(emptyOrganizationDraft());
            onClose();
        } catch (err) {
            setError(err instanceof Error && err.message ? err.message : "Could not create the organization");
        } finally {
            setBusy(false);
        }
    };

    if (!open) return null;

    return (
        <Modal
            open={open}
            onClose={close}
            title="Add a new organization"
            subtitle="You'll be its administrator, and the console switches to it once it exists."
            footer={
                <>
                    <Btn tone="quiet" onClick={close} disabled={busy}>
                        Cancel
                    </Btn>
                    <Btn onClick={create} disabled={busy || !draft.name.trim()}>
                        {busy ? "Creating…" : "Create organization"}
                    </Btn>
                </>
            }
        >
            <OrganizationFields draft={draft} onChange={setDraft} disabled={busy} autoFocus />
            {error && <p className="text-[12.5px] text-[var(--crit)]">{error}</p>}
        </Modal>
    );
};
